import { useCallback, useEffect, useState } from 'react'
import { ChartPt, Station } from 'src/models'
import { last, mapValues, uniqBy } from 'lodash'
import { elmIdLookup, getStation } from 'src/api'
import { useParams } from 'react-router-dom'
import { useTimer } from 'src/hooks/useTimer'

export const useViewModel = () => {
  const { stnId } = useParams<{ stnId: string }>()
  const [station, setStation] = useState<Station | undefined>()
  const [obsLookup, setObsLookup] = useState<Record<string, ChartPt[]>>({})
  const [latestObsLookup, setLatestObsLookup] = useState<Record<string, ChartPt | undefined>>({})

  const load = useCallback(
    async (stnId: string) => {
      const { station: stn, obs } = await getStation(stnId)
      setStation(stn)
      setObsLookup(embedRH2T(obs))
      setLatestObsLookup(mapValues(obs, v => last(v.filter(o => o.t < Date.now()))))
    },
    [stnId],
  )

  useTimer(
    () => {
      if (stnId === undefined) return
      load(stnId)
    },
    { interval: 60_000 },
  )

  useEffect(() => {
    if (stnId === undefined) return
    load(stnId)
    // test()
  }, [stnId])

  return {
    station,
    obsLookup,
    latestObsLookup,
  }
}

const embedRH2T = (obs: Record<string, ChartPt[]>): Record<string, ChartPt[]> => {
  const rh = obs[elmIdLookup.humidity]
  const t = obs[elmIdLookup.temperature]
  if (rh === undefined || t === undefined) return obs
  const mixed = uniqBy([...rh, ...t], 't').sort((a, b) => a.t - b.t)
  const rh2t = mixed.map(v => {
    const rhV = rh.find(o => o.t === v.t)?.v
    const tV = t.find(o => o.t === v.t)?.v
    return {
      t: v.t,
      v: tV,
      v2: rhV,
    }
  })

  return {
    ...obs,
    [elmIdLookup.temperature]: rh2t,
  }
}
